<script setup lang="ts">
import type { Component } from 'vue'
import type { TransferVisualStatus } from '~/constants/receiptModel'
import IconCheck from '~icons/material-symbols/check'
import IconClose from '~icons/material-symbols/close'
import IconEventAvailable from '~icons/material-symbols/event-available'
import IconScheduleOutline from '~icons/material-symbols/schedule-outline'

const props = defineProps<{
  status: TransferVisualStatus
}>()

const { t } = useI18n()

interface BadgeConfig {
  icon: Component
  containerClass: string
  iconClass: string
}

const BADGE_CONFIG: Record<TransferVisualStatus, BadgeConfig> = {
  analysis: {
    icon: IconScheduleOutline,
    containerClass: 'bg-neutral-high-light border-neutral-high-dark',
    iconClass: 'text-brand-primary-light',
  },
  pending: {
    icon: IconScheduleOutline,
    containerClass: 'bg-feedback-warning-light border-feedback-warning-medium',
    iconClass: 'text-[#b37f00]',
  },
  scheduled: {
    icon: IconEventAvailable,
    containerClass: 'bg-feedback-informative-light border-feedback-informative-medium',
    iconClass: 'text-feedback-informative-pure',
  },
  paid: {
    icon: IconCheck,
    containerClass: 'bg-feedback-positive-light border-feedback-positive-medium',
    iconClass: 'text-feedback-positive-pure',
  },
  error: {
    icon: IconClose,
    containerClass: 'bg-feedback-negative-light border-feedback-negative-medium',
    iconClass: 'text-feedback-negative-pure',
  },
  cancelled: {
    icon: IconClose,
    containerClass: 'bg-neutral-high-medium border-[#a5a5a5]',
    iconClass: 'text-neutral-low-light',
  },
}

const config = computed(() => BADGE_CONFIG[props.status] ?? BADGE_CONFIG.analysis)
const label = computed(() => t(`receipt-model.receivables.status.${props.status}`))
</script>

<template>
  <span
    role="img"
    class="transfer-status-badge"
    :class="config.containerClass"
    :aria-label="label"
    :title="label"
    :data-status="status"
  >
    <component
      :is="config.icon"
      class="icon-size-small"
      :class="config.iconClass"
      aria-hidden="true"
    />
  </span>
</template>

<style scoped lang="scss">
.transfer-status-badge {
  @apply inline-flex items-center justify-center shrink-0 p-quark rounded-full border-1;
}
</style>
